"use client";

import Link from "next/link";
import {usePathname} from "next/navigation";
import {useState, useEffect, useCallback, useRef} from "react";
import type {Root, Node, Item, Folder, Separator} from "fumadocs-core/page-tree";
import {cn} from "@/common/lib/utils";
import {ChevronDownFilled} from "@/common/components/fluent-icons";
import {isWebSocketDocsPath} from "@/features/docs/lib/websocket-docs";

function useHash() {
  const pathname = usePathname();
  const [hash, setHash] = useState('');

  const update = useCallback(() => {
    setHash(window.location.hash);
  }, []);

  // Re-read hash whenever pathname changes (cross-page navigation)
  useEffect(() => {
    // After Next.js navigation, the hash may take a moment to appear
    update();
    const t = setTimeout(update, 150);
    return () => clearTimeout(t);
  }, [pathname, update]);

  useEffect(() => {
    update();
    window.addEventListener('hashchange', update);

    // For same-page Link clicks, the hash updates after a microtask
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement)?.closest?.('a');
      if (anchor?.href?.includes('#')) {
        setTimeout(update, 0);
        setTimeout(update, 100);
      }
    };
    window.addEventListener('click', onClick, true);

    return () => {
      window.removeEventListener('hashchange', update);
      window.removeEventListener('click', onClick, true);
    };
  }, [update]);

  return hash;
}

/** Walk up the DOM to find the nearest ancestor that actually scrolls vertically. */
function getScrollParent(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

export interface DocsSectionTrees {
  api: Node[];
  mcp: Node[];
  docs: Node[];
}

export interface DocsSidebarNavProps {
  tree: Root;
  sectionTrees?: DocsSectionTrees;
  setOpen?: (open: boolean) => void;
  mobile?: boolean;
}

const DOCS_DEFAULT_PAGE = '/docs/api/quickstart';

/** Groups root-level nodes by category separators (those with $id starting with 'category:') */
function groupByCategory(nodes: Node[]): { separator?: Separator; items: Node[] }[] {
  const groups: { separator?: Separator; items: Node[] }[] = [];
  let current: { separator?: Separator; items: Node[] } = { items: [] };

  for (const node of nodes) {
    if (node.type === 'separator' && node.$id?.startsWith('category:')) {
      if (current.separator || current.items.length > 0) {
        groups.push(current);
      }
      current = { separator: node, items: [] };
    } else {
      current.items.push(node);
    }
  }
  if (current.separator || current.items.length > 0) {
    groups.push(current);
  }

  return groups;
}

const API_SECTION_PATHS = new Set(['/docs/api/rate-limits']);

function getActiveSection(pathname: string): 'api' | 'mcp' | 'docs' {
  // WebSocket Server + SDK pages live under /docs/api/* URLs but belong to General Docs.
  if (isWebSocketDocsPath(pathname)) return 'docs';
  if (pathname === '/docs/api' || pathname.startsWith('/docs/api/')) return 'api';
  if (API_SECTION_PATHS.has(pathname)) return 'api';
  if (pathname.startsWith('/docs/mcp')) return 'mcp';
  return 'docs';
}

export function DocsSidebarNav({tree, sectionTrees, setOpen, mobile}: DocsSidebarNavProps) {
  const rawPathname = usePathname();
  const pathname = rawPathname === '/docs' ? DOCS_DEFAULT_PAGE : rawPathname;
  const hash = useHash();
  const fullUrl = `${pathname}${hash}`;

  // Use section-filtered tree if available
  const activeChildren = sectionTrees
    ? sectionTrees[getActiveSection(pathname)]
    : tree.children;

  const groups = groupByCategory(activeChildren);

  return (
    <nav className="w-full">
      {groups.map((group, gi) => (
        <div key={gi}>
          {group.separator && (
            <SidebarSeparator node={group.separator as Separator} mobile={mobile} isFirst={gi === 0} />
          )}
          <div className="flex flex-col gap-[3px]">
            {group.items.map((node, i) => (
              <SidebarNode
                key={nodeKey(node, i)}
                node={node}
                currentUrl={fullUrl}
                pathname={pathname}
                setOpen={setOpen}
                mobile={mobile}
                nested={false}
                isFirst={gi === 0 && i === 0}
              />
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

interface SidebarNodeProps {
  node: Node;
  currentUrl: string;
  pathname: string | null;
  setOpen?: (open: boolean) => void;
  mobile?: boolean;
  nested: boolean;
  isFirst?: boolean;
  isFirstInFolder?: boolean;
}

function SidebarNode({node, currentUrl, pathname, setOpen, mobile, nested, isFirst, isFirstInFolder}: SidebarNodeProps) {
  switch (node.type) {
    case "separator":
      return <SidebarSeparator node={node} mobile={mobile} isFirst={isFirst} />;
    case "page":
      return <SidebarItem node={node} currentUrl={currentUrl} pathname={pathname} setOpen={setOpen} mobile={mobile} nested={nested} isFirstInFolder={isFirstInFolder} />;
    case "folder":
      return <SidebarFolder node={node} currentUrl={currentUrl} pathname={pathname} setOpen={setOpen} mobile={mobile} nested={nested} />;
  }
}

function SidebarSeparator({node, mobile, isFirst}: { node: Separator; mobile?: boolean; isFirst?: boolean }) {
  if (!node.name) return null;

  const isCategory = node.$id?.startsWith('category:');

  if (isCategory) {
    return (
      <h4 className={cn(
        "uppercase tracking-wider text-muted-foreground font-medium mb-[5px]",
        mobile ? "text-xs" : "text-[11px]",
        !isFirst && "mt-6"
      )}>
        {node.name}
      </h4>
    );
  }

  return (
    <h4 className={cn(
      "font-semibold text-foreground px-1.5",
      mobile ? "text-base mb-0.5" : "text-sm mb-0.5",
      !isFirst && "mt-4"
    )}>
      {node.name}
    </h4>
  );
}

const ACTIVE_METHOD_STYLES: Record<string, string> = {
  get: 'bg-green-500/15 text-primary font-medium',
  post: 'bg-blue-500/15 text-primary font-medium',
  put: 'bg-yellow-500/15 text-primary font-medium',
  patch: 'bg-orange-500/15 text-primary font-medium',
  delete: 'bg-red-500/15 text-primary font-medium',
};

function SidebarItem({node, currentUrl, pathname, setOpen, mobile, nested, isFirstInFolder}: {
  node: Item;
  currentUrl: string;
  pathname: string | null;
  setOpen?: (open: boolean) => void;
  mobile?: boolean;
  nested: boolean;
  isFirstInFolder?: boolean;
}) {
  const hasHash = currentUrl.includes('#');
  const nodeHasHash = node.url.includes('#');
  const isActive = nodeHasHash
    ? currentUrl === node.url || (!hasHash && isFirstInFolder && node.url.split('#')[0] === pathname)
    : !hasHash && pathname === node.url;

  const isDeprecated = node.$id?.includes('deprecated') ?? false;
  const method = node.$id?.startsWith('method:') ? node.$id.slice(7).replace(':deprecated', '') : undefined;
  const activeStyle = method && isActive ? ACTIVE_METHOD_STYLES[method] : undefined;

  const handleClick = useCallback((e: React.MouseEvent) => {
    setOpen?.(false);

    const [targetPath, hash] = node.url.split('#');
    // Only intercept same-page hash navigation
    if (hash && targetPath === pathname) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) {
        // Update URL hash and notify listeners
        window.history.pushState(null, '', node.url);
        window.dispatchEvent(new HashChangeEvent('hashchange'));

        // Find the nearest actually-scrollable ancestor
        const scrollContainer = getScrollParent(el);
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const top = elRect.top - containerRect.top + scrollContainer.scrollTop - 24;
          scrollContainer.scrollTo({ top, behavior: 'instant' });
        } else {
          const top = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, behavior: 'instant' });
        }
      }
    }
  }, [node.url, pathname, setOpen]);

  return (
    <Link
      href={node.url}
      onClick={handleClick}
      data-active={isActive || undefined}
      className={cn(
        "flex items-center transition-colors font-light rounded-md px-1.5",
        mobile ? "py-1.5 text-base" : "py-[5px] text-sm",
        isActive
          ? activeStyle ?? "bg-accent/60 text-primary font-medium"
          : "text-muted-foreground hover:text-primary",
      )}
    >
      <span className={cn("flex-1 min-w-0 truncate")}>{node.name}</span>
    </Link>
  );
}

/** Stripe-style group: label acts as a link, with a chevron to toggle; auto-expands when active */
function SidebarGroup({node, currentUrl, pathname, setOpen, mobile}: {
  node: Folder;
  currentUrl: string;
  pathname: string | null;
  setOpen?: (open: boolean) => void;
  mobile?: boolean;
}) {
  const isChildActive = isDescendantActive(node, pathname);
  const isSelfActive = !!(node.index?.url && pathname === node.index.url);
  const isGroupActive = isSelfActive || isChildActive;

  // Auto-open when active, but let the chevron toggle it independently otherwise.
  const [expanded, setExpanded] = useState(isGroupActive);
  useEffect(() => {
    if (isGroupActive) setExpanded(true);
  }, [isGroupActive]);

  // Only mark self as data-active when on the group intro page (no endpoint hash),
  // so the parent indicator doesn't double-highlight alongside a child endpoint
  const hasEndpointHash = currentUrl.includes('#') && !currentUrl.endsWith('#introduction');
  const showSelfActive = isSelfActive && !hasEndpointHash;

  const hasChildren = node.children.length > 0;

  return (
    <div>
      <div className="flex items-center">
        <Link
          href={node.index!.url}
          onClick={() => setOpen?.(false)}
          data-active={showSelfActive || undefined}
          className={cn(
            "flex-1 min-w-0 flex items-center transition-colors font-light rounded-md px-1.5",
            mobile ? "py-1.5 text-base" : "py-[5px] text-sm",
            showSelfActive
              ? "bg-accent/50 text-primary font-normal"
              : "text-muted-foreground hover:text-primary",
          )}
        >
          <span className="flex-1 min-w-0 truncate">{node.name}</span>
        </Link>
        {hasChildren && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="shrink-0 p-1 cursor-pointer"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            <ChevronDownFilled
              fontSize={14}
              className={cn(
                "text-muted-foreground/50 transition-transform duration-200",
                expanded && "-rotate-180"
              )}
            />
          </button>
        )}
      </div>

      {hasChildren && (
        <div
          className="grid transition-[grid-template-rows] duration-200 ease-out"
          style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div className="mt-1">
              <SidebarChildrenWithIndicator
                node={node}
                currentUrl={currentUrl}
                pathname={pathname}
                setOpen={setOpen}
                mobile={mobile}
                isGroup
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarFolder({node, currentUrl, pathname, setOpen, mobile, nested}: {
  node: Folder;
  currentUrl: string;
  pathname: string | null;
  setOpen?: (open: boolean) => void;
  mobile?: boolean;
  nested: boolean;
}) {
  const isChildActive = isDescendantActive(node, pathname);
  const isSelfActive = !!(node.index?.url && pathname === node.index.url);
  // Respect each folder's defaultOpen (API/MCP tag folders set it false → start collapsed).
  // Previously top-level folders were force-opened, which ignored defaultOpen: false.
  const [expanded, setExpanded] = useState(node.defaultOpen ?? true);

  useEffect(() => {
    if (isChildActive) setExpanded(true);
  }, [isChildActive]);

  const handleClick = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const forceGroup = node.$id?.startsWith('group:');
  if ((nested || forceGroup) && node.index?.url) {
    return (
      <SidebarGroup
        node={node}
        currentUrl={currentUrl}
        pathname={pathname}
        setOpen={setOpen}
        mobile={mobile}
      />
    );
  }

  return (
    <div>
      <div className={cn(
        "flex items-center",
        !nested && "mt-0.5",
      )}>
        <button
          onClick={handleClick}
          className={cn(
            "flex-1 min-w-0 truncate text-left cursor-pointer transition-colors rounded-md",
            nested && "px-1.5",
            mobile ? "py-1.5 text-base" : "py-[5px] text-sm",
            nested
              ? isSelfActive
                ? "bg-accent/50 text-primary font-normal"
                : "text-muted-foreground hover:text-primary font-light"
              : "font-semibold text-foreground",
          )}
        >
          {node.name}
        </button>
        <button
          onClick={handleClick}
          className="shrink-0 p-1 cursor-pointer"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          <ChevronDownFilled
            fontSize={14}
            className={cn(
              "text-muted-foreground/50 transition-transform duration-200",
              expanded && "-rotate-180"
            )}
          />
        </button>
      </div>

      {node.children.length > 0 && (
        <div
          className="grid transition-[grid-template-rows] duration-200 ease-out"
          style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <SidebarChildrenWithIndicator
              node={node}
              currentUrl={currentUrl}
              pathname={pathname}
              setOpen={setOpen}
              mobile={mobile}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarChildrenWithIndicator({node, currentUrl, pathname, setOpen, mobile, isGroup}: {
  node: Folder;
  currentUrl: string;
  pathname: string | null;
  setOpen?: (open: boolean) => void;
  mobile?: boolean;
  isGroup?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{top: number; height: number} | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const activeEl = container.querySelector<HTMLElement>('[data-active="true"]');
    if (activeEl) {
      // Only show indicator if the active element is a direct child of this container,
      // not nested inside a sub-folder's own SidebarChildrenWithIndicator
      const isDirectChild = activeEl.parentElement === container ||
        activeEl.closest('[data-sidebar-children]') === container;
      if (isDirectChild) {
        const containerRect = container.getBoundingClientRect();
        const activeRect = activeEl.getBoundingClientRect();
        setIndicatorStyle({
          top: activeRect.top - containerRect.top,
          height: activeRect.height,
        });
      } else {
        setIndicatorStyle(null);
      }
    } else {
      setIndicatorStyle(null);
    }
  }, [currentUrl, pathname]);

  return (
    <div ref={containerRef} data-sidebar-children className="relative ml-0 pl-0 xl:ml-2 xl:pl-3 flex flex-col gap-[3px]">
      {/* Static border line — hidden on small sidebars */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-border hidden xl:block" />
      {/* Active indicator */}
      {indicatorStyle && (
        <div
          className="absolute left-0 w-px bg-primary transition-all duration-200 ease-out hidden xl:block"
          style={{ top: indicatorStyle.top, height: indicatorStyle.height }}
        />
      )}
      {node.children.map((child, i) => (
        <SidebarNode
          key={nodeKey(child, i)}
          node={child}
          currentUrl={currentUrl}
          pathname={pathname}
          setOpen={setOpen}
          mobile={mobile}
          nested
          isFirstInFolder={!isGroup && i === 0}
        />
      ))}
    </div>
  );
}

function isDescendantActive(node: Node, pathname: string | null): boolean {
  if (!pathname) return false;
  if (node.type === "page") {
    const baseUrl = node.url.split('#')[0];
    return baseUrl === pathname;
  }
  if (node.type === "folder") {
    if (node.index?.url === pathname) return true;
    return node.children.some((child) => isDescendantActive(child, pathname));
  }
  return false;
}

function nodeKey(node: Node, index: number): string {
  if (node.type === "page") return `${node.url}-${index}`;
  if (node.type === "folder") return node.index?.url ?? `folder-${index}`;
  return `sep-${index}`;
}
